export const STAGES = ['dev', 'staging'] as const;

export type Stage = (typeof STAGES)[number];

export function isStage(value: unknown): value is Stage {
  return typeof value === 'string' && (STAGES as readonly string[]).includes(value);
}

export const KEY_VAULT_ANNOTATION_PREFIX = 'everdriven.com/key-vault-';

export function keyVaultAnnotation(stage: Stage): string {
  return `${KEY_VAULT_ANNOTATION_PREFIX}${stage}`;
}

/** A Secret key a new mapping may use. `-` allows keys such as
 * `RhapsodyR2B__Tenants__<uuid>__ConnectionString`. */
export const NEW_SECRET_KEY = /^[A-Za-z_][A-Za-z0-9_-]*$/;

/** The Kubernetes Secret key charset, for keys that already exist. */
export const SECRET_KEY = /^[-._a-zA-Z0-9]+$/;

/** Key Vault ignores case, so the portal only ever uses the lowercase form. */
export const VAULT_NAME = /^[a-z0-9-]{1,127}$/;

/** Kubernetes refuses Secret keys longer than this. */
export const MAX_SECRET_KEY_LENGTH = 253;

/** Key Vault refuses secret values larger than 25 KB. */
export const MAX_VALUE_BYTES = 25 * 1024;

/** CLE-966 D3: lowercase, and each run of `_` becomes one `-`. */
export function vaultNameFor(secretKey: string): string {
  return secretKey.toLowerCase().replace(/_+/g, '-');
}

export function isNewSecretKey(secretKey: string): boolean {
  return (
    secretKey.length <= MAX_SECRET_KEY_LENGTH &&
    NEW_SECRET_KEY.test(secretKey) &&
    VAULT_NAME.test(vaultNameFor(secretKey))
  );
}

export function isExistingSecretKey(secretKey: string): boolean {
  return (
    secretKey.length <= MAX_SECRET_KEY_LENGTH &&
    SECRET_KEY.test(secretKey) &&
    secretKey !== '.' &&
    secretKey !== '..'
  );
}

export function valueByteLength(value: string): number {
  return new TextEncoder().encode(value).length;
}
